import { z } from 'zod'

const id = z.string().min(1)
const amount = z.number().int().nonnegative()
export const paymentProductSchema = z.looseObject({
  id,
  name: z.string().min(1),
  amount,
  currency: z.literal('KRW'),
  isTest: z.boolean().optional(),
  kind: z.string().optional(),
  active: z.boolean().optional(),
  credits: amount.optional(),
  bonus: amount.optional(),
  baseAmount: amount.optional(),
  options: z
    .looseObject({
      badge: z.string().optional(),
      days: z.number().int().positive().optional(),
      bonusDays: z.number().int().positive().optional(),
      sortOrder: z.number().optional(),
    })
    .optional(),
  firstCharge: z
    .object({
      percent: z.number().nonnegative(),
      eligible: z.boolean(),
      bonus: amount,
    })
    .optional(),
})
export const paymentModelSchema = z.enum(['WEB', 'MOB'])
const payMethodSchema = z.enum([
  'CARD',
  'TRANSFER',
  'VIRTUAL_ACCOUNT',
  'MOBILE_PHONE',
])
const optionBase = {
  id,
  label: z.string().min(1),
  available: z.boolean(),
  models: z.array(paymentModelSchema),
}
const normalizedPaymentOptionSchema = z.discriminatedUnion('kind', [
  z.object({
    ...optionBase,
    kind: z.literal('payment'),
    method: payMethodSchema,
    easyPay: z
      .enum(['TOSSPAY', 'NAVERPAY', 'KAKAOPAY', 'PAYCO', 'SAMSUNGPAY'])
      .optional(),
  }),
  z.object({ ...optionBase, kind: z.literal('brandpay') }),
])
export const tossStandardPaymentMethods = {
  card: 'CARD',
  bank: 'TRANSFER',
  vacnt: 'VIRTUAL_ACCOUNT',
  phone: 'MOBILE_PHONE',
} as const
export const tossStandardOptionIdSchema = z.enum([
  'card',
  'bank',
  'vacnt',
  'phone',
])
const paymentOptionSchema = z
  .union([
    normalizedPaymentOptionSchema,
    z
      .object({
        id: tossStandardOptionIdSchema,
        name: z.string().min(1),
        type: z.literal('standard'),
        payMethod: payMethodSchema,
        available: z.boolean(),
        models: z.array(paymentModelSchema),
      })
      .refine(
        (option) => tossStandardPaymentMethods[option.id] === option.payMethod
      )
      .transform((option) => ({
        id: option.id,
        label: option.name,
        kind: 'payment' as const,
        method: option.payMethod,
        available: option.available,
        models: option.models,
      })),
    z
      .object({
        id: z.literal('brandpay'),
        name: z.string().min(1),
        type: z.literal('brandpay'),
        available: z.boolean(),
        models: z.array(paymentModelSchema),
      })
      .transform((option) => ({
        id: option.id,
        label: option.name,
        kind: 'brandpay' as const,
        available: option.available,
        models: option.models,
      })),
  ])
  .pipe(normalizedPaymentOptionSchema)
export const paymentProductsSchema = z.looseObject({
  products: z.array(paymentProductSchema),
  providerConfigured: z.boolean(),
  provider: z.string().nullable().default(null),
  testMode: z.boolean(),
  paymentOptions: z.array(paymentOptionSchema).default([]),
})
export const refundAccountSchema = z.object({
  bankCode: z.string().trim().min(1),
  number: z.string().trim().min(1),
  holder: z.string().trim().min(1),
})
export const refundSchema = z.looseObject({
  id,
  orderId: id,
  amount,
  reason: z.string(),
  status: z.string(),
  createdAt: z.string(),
})
const normalizedVirtualAccountSchema = z.looseObject({
  number: z.string(),
  bankName: z.string(),
  expiresAt: z.string(),
})
const virtualAccountSchema = z
  .union([
    normalizedVirtualAccountSchema,
    z
      .object({
        bankCode: z.string().min(1),
        accountNumber: z.string(),
        customerName: z.string(),
        dueDate: z.string(),
      })
      .transform((account) => ({
        number: account.accountNumber,
        bankName: `은행 코드 ${account.bankCode}`,
        expiresAt: account.dueDate,
      })),
  ])
  .nullable()
const paymentMethodNames = new Map([
  ['카드', 'CARD'],
  ['간편결제', 'CARD'],
  ['계좌이체', 'TRANSFER'],
  ['가상계좌', 'VIRTUAL_ACCOUNT'],
  ['휴대폰', 'MOBILE_PHONE'],
])
export const paymentOrderSchema = z.looseObject({
  id,
  product: paymentProductSchema,
  provider: z.string().min(1),
  isTest: z.boolean(),
  refundedAmount: amount,
  createdAt: z.string(),
  status: z.enum([
    'pending',
    'awaiting_deposit',
    'paid',
    'partially_refunded',
    'refunded',
    'failed',
  ]),
  payment: z.looseObject({
    sandbox: z.boolean(),
    phase: z.string(),
    payMethod: z
      .string()
      .transform((method) => paymentMethodNames.get(method) ?? method),
    confirmedPaymentOptionId: z.string().nullable().optional(),
    easyPayName: z.string().nullable().optional(),
    resultCode: z.string().nullable().optional(),
    resultMessage: z.string().nullable().optional(),
    virtualAccount: virtualAccountSchema,
  }),
  refunds: z.array(refundSchema).optional(),
})
export const paymentOrderDetailSchema = paymentOrderSchema.safeExtend({
  refunds: z.array(refundSchema),
})
export const tossClientKeySchema = z.string().regex(/^(test|live)_ck_[^\s]+$/)
export const tossCustomerKeySchema = z
  .string()
  .min(2)
  .max(50)
  .regex(/^[A-Za-z0-9_\-=.@]+$/)
const checkoutVirtualAccountSchema = z.object({
  cashReceipt: z.object({ type: z.literal('소득공제') }),
})
export const tossCheckoutEnvelopeSchema = z.object({
  orderId: id,
  provider: z.literal('toss'),
  checkout: z.object({
    sdkUrl: z.literal('https://js.tosspayments.com/v2/standard'),
    clientKey: tossClientKeySchema,
    customerKey: tossCustomerKeySchema,
    request: z.object({
      method: payMethodSchema,
      amount: z.object({
        currency: z.literal('KRW'),
        value: amount.positive(),
      }),
      orderId: id,
      orderName: z.string().min(1).max(100),
      successUrl: z.string().url(),
      failUrl: z.string().url(),
      customerName: z.string().max(100).optional(),
      virtualAccount: checkoutVirtualAccountSchema.optional(),
    }),
  }),
})
export const tossBrandpayCheckoutEnvelopeSchema = z.object({
  orderId: id,
  provider: z.literal('toss'),
  checkout: z.object({
    sdkUrl: z.literal('https://js.tosspayments.com/v2/standard'),
    clientKey: tossClientKeySchema,
    customerKey: tossCustomerKeySchema,
    redirectUrl: z.string().url(),
    request: z.object({
      amount: z.object({
        currency: z.literal('KRW'),
        value: amount.positive(),
      }),
      orderId: id,
      orderName: z.string().min(1).max(100),
      methodId: id.optional(),
      customerName: z.string().max(100).optional(),
    }),
  }),
})
export const paymentAuthorizationSchema = z.object({
  paymentKey: z.string().min(1).max(200),
  orderId: id,
  amount: z.object({ currency: z.literal('KRW'), value: amount.positive() }),
})
export const paymentCheckoutSchema = z
  .object({
    orderId: z.string().regex(/^[A-Za-z0-9_=-]{6,64}$/),
    orderName: z.string().min(1).max(100),
    amount: amount.positive(),
    currency: z.literal('KRW'),
    clientKey: tossClientKeySchema,
    customerKey: tossCustomerKeySchema,
    paymentOption: paymentOptionSchema,
    successUrl: z.string().url().optional(),
    failUrl: z.string().url().optional(),
    methodId: id.optional(),
    brandpayRedirectUrl: z.string().url().optional(),
    customerName: z.string().max(100).optional(),
    customerEmail: z.email().max(100).optional(),
    customerMobilePhone: z
      .string()
      .regex(/^\d{8,15}$/)
      .optional(),
    virtualAccount: checkoutVirtualAccountSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (
      value.paymentOption.kind === 'payment' &&
      (!value.successUrl || !value.failUrl)
    )
      ctx.addIssue({
        code: 'custom',
        path: ['successUrl'],
        message: '결제 복귀 주소가 없습니다.',
      })
    if (value.paymentOption.kind === 'brandpay' && !value.brandpayRedirectUrl)
      ctx.addIssue({
        code: 'custom',
        path: ['brandpayRedirectUrl'],
        message: '브랜드페이 인증 주소가 없습니다.',
      })
    if (
      value.paymentOption.kind === 'payment' &&
      value.paymentOption.easyPay &&
      value.paymentOption.method !== 'CARD'
    )
      ctx.addIssue({
        code: 'custom',
        path: ['paymentOption'],
        message: '간편결제 수단이 일치하지 않습니다.',
      })
  })
export const purchaseAttemptSchema = z.object({
  version: z.literal(1),
  ownerId: id,
  requestId: z.uuid(),
  productId: id,
  orderId: id.optional(),
})
export const refundAttemptSchema = z.object({
  version: z.literal(1),
  ownerId: id,
  orderId: id,
  requestId: z.uuid(),
  fingerprint: z.string(),
})
export type PaymentProduct = z.infer<typeof paymentProductSchema>
export type PaymentProducts = z.infer<typeof paymentProductsSchema>
export type PaymentOrder = z.infer<typeof paymentOrderSchema>
export type PaymentRefund = z.infer<typeof refundSchema>
export type PaymentCheckout = z.infer<typeof paymentCheckoutSchema>
export type PaymentModel = z.infer<typeof paymentModelSchema>
export type PurchaseAttempt = z.infer<typeof purchaseAttemptSchema>
export type RefundAccount = z.infer<typeof refundAccountSchema>
